import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchExpiredContracts } from '../api/stats';
import {
    fetchScheduledNotifications,
    createScheduledNotification,
    type NotificationFrequency,
    type NotificationMethod
} from '../api/alerts';
import { 
    Calendar, 
    Clock, 
    Bell, 
    Mail, 
    AlertTriangle,
    CheckCircle,
    Download,
    ArrowLeft
} from 'lucide-react';

interface Contrat {
  id: number;
  contract_id?: number | null;
  employee_pk?: number | null;
  nom: string;
  poste: string;
  departement: string;
  dateDebut: string;
  dateFin: string;
  email?: string;
  photo?: string;
}

interface Rappel {
  id: number;
  contratIds: number[];
  dateRappel: string;
  heure: string;
  frequence: 'ponctuel' | 'quotidien' | 'hebdomadaire';
  methode: ('email' | 'notification')[];
  message: string;
  statut: 'actif' | 'termine';
}

// Mapper pour convertir les types de fréquence
const mapFrequencyToApi = (freq: 'ponctuel' | 'quotidien' | 'hebdomadaire'): NotificationFrequency => {
    switch (freq) {
        case 'ponctuel': return 'ONCE';
        case 'quotidien': return 'DAILY';
        case 'hebdomadaire': return 'WEEKLY';
        default: return 'ONCE';
    }
};

const mapFrequencyFromApi = (freq: string): 'ponctuel' | 'quotidien' | 'hebdomadaire' => {
    switch (freq) {
        case 'ONCE': return 'ponctuel';
        case 'DAILY': return 'quotidien';
        case 'WEEKLY': return 'hebdomadaire';
        default: return 'ponctuel';
    }
};

const mapNotificationMethod = (methode: ('email' | 'notification')[]): NotificationMethod => {
    if (methode.includes('email') && methode.includes('notification')) return 'BOTH';
    if (methode.includes('email')) return 'EMAIL';
    return 'IN_APP';
};

const ContratsExpires: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // États pour la gestion des rappels
    const [contratsSelectionnes, setContratsSelectionnes] = useState<number[]>([]);
    const [modalRappelOpen, setModalRappelOpen] = useState(false);
    const [rappelPlanifies, setRappelPlanifies] = useState<Rappel[]>([]);
    const [nouveauRappel, setNouveauRappel] = useState<Omit<Rappel, 'id' | 'statut'>>({
        contratIds: [],
        dateRappel: new Date().toISOString().split('T')[0],
        heure: '09:00',
        frequence: 'ponctuel',
        methode: ['notification'],
        message: 'Rappel : Contrat expiré depuis longtemps'
    });

    // État des données et chargement API
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<Contrat[]>([]);

    // Charger les rappels planifiés
    const loadRappels = async () => {
        try {
            const notifications = await fetchScheduledNotifications({ status: 'ACTIVE' });
            const mapped: Rappel[] = notifications.map(n => ({
                id: n.id,
                contratIds: [], // Pas utilisé dans l'affichage
                dateRappel: n.scheduled_date,
                heure: n.scheduled_time,
                frequence: mapFrequencyFromApi(n.frequency),
                methode: ['notification'], // Simplifié pour l'affichage
                message: n.title,
                statut: n.status === 'ACTIVE' ? 'actif' : 'termine'
            }));
            setRappelPlanifies(mapped);
        } catch (err: any) {
            console.error('Erreur chargement rappels:', err);
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const toAbsolute = (url?: string | null) => {
                    if (!url) return '';
                    if (/^https?:\/\//i.test(url)) return url;
                    const apiBase = import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, '') || '';
                    return `${apiBase}${url.startsWith('/') ? url : `/${url}`}`;
                };

                const list = await fetchExpiredContracts();
                if (!mounted) return;
                // Adapter au type local Contrat
                const mapped: Contrat[] = list.map(item => ({
                    id: item.id,
                    contract_id: item.contract_id,
                    employee_pk: item.id ?? null,
                    nom: item.employee_name,
                    poste: item.position_display || '',
                    departement: item.department_name || '—',
                    dateDebut: item.contract_start_date || '',
                    dateFin: item.contract_end_date,
                    email: item.email || '',
                    photo: toAbsolute(item.photo)
                }));
                setRows(mapped);

                // Charger les rappels planifiés
                await loadRappels();
            } catch (e: any) {
                if (!mounted) return;
                setError(e?.message || 'Erreur lors du chargement des contrats expirés');
                setRows([]);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Protection de la route
    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }

    // Calculer les jours écoulés depuis l'expiration
    const calculerJoursExpires = (dateFin: string): number => {
        const aujourdhui = new Date();
        const dateFinContrat = new Date(dateFin);
        const difference = aujourdhui.getTime() - dateFinContrat.getTime();
        return Math.floor(difference / (1000 * 3600 * 24));
    };

    // Formater la date en français
    const formaterDate = (date: string): string => {
        // Une date absente ne doit pas produire « Invalid Date ».
        if (!date) return '';
        const valeur = new Date(date);
        if (Number.isNaN(valeur.getTime())) return '';
        return valeur.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Gestion de la sélection des contrats
    const toggleSelectionContrat = (contratId: number) => {
        setContratsSelectionnes(prev => 
            prev.includes(contratId) 
                ? prev.filter(id => id !== contratId)
                : [...prev, contratId]
        );
    };

    // Ouvrir le modal de planification
    const ouvrirModalRappel = () => {
        if (contratsSelectionnes.length === 0) {
            alert('Veuillez sélectionner au moins un employé');
            return;
        }

        const contractIds = contratsSelectionnes
            .map(id => {
                const contrat = rows.find(c => c.id === id);
                return contrat?.contract_id;
            })
            .filter((contractId): contractId is number => contractId != null);

        const employesInfo = contratsSelectionnes
            .map(id => {
                const contrat = rows.find(c => c.id === id);
                if (!contrat) return null;
                return `${contrat.nom} (fin le ${formaterDate(contrat.dateFin)})`;
            })
            .filter(Boolean)
            .join(', ');

        const messageDefaut = `Rappel : Contrat expiré pour ${employesInfo}`;

        setNouveauRappel(prev => ({
            ...prev,
            contratIds: contractIds,
            message: messageDefaut
        }));
        setModalRappelOpen(true);
    };

    // Planifier un nouveau rappel
    const planifierRappel = async () => {
        try {
            const notificationData: any = {
                scheduled_date: nouveauRappel.dateRappel,
                scheduled_time: nouveauRappel.heure,
                frequency: mapFrequencyToApi(nouveauRappel.frequence),
                title: `Rappel contrats expirés - ${contratsSelectionnes.length} employé(s)`,
                message: nouveauRappel.message,
                notification_method: mapNotificationMethod(nouveauRappel.methode)
            };

            if (nouveauRappel.contratIds.length > 0) {
                notificationData.contract_ids = nouveauRappel.contratIds;
            }

            await createScheduledNotification(notificationData);
            await loadRappels();
            setModalRappelOpen(false);
            setContratsSelectionnes([]);
            alert(`Rappel planifié avec succès pour ${contratsSelectionnes.length} employé(s)`);
        } catch (err: any) {
            console.error('Erreur planification rappel:', err);
            const errorMsg = err?.response?.data?.contract_ids?.[0] || err?.message || 'Erreur lors de la planification du rappel';
            alert(`Erreur: ${errorMsg}`);
        }
    };

    // Obtenir le nom des contrats sélectionnés
    const getNomsContratsSelectionnes = () => {
        return contratsSelectionnes.map(id =>
            rows.find((c: Contrat) => c.id === id)?.nom
        ).filter(Boolean).join(', ');
    };

    // Exporter la liste des contrats en CSV
    const exporterListeCSV = () => {
        if (rows.length === 0) {
            alert('Aucun contrat à exporter');
            return;
        }

        // Mêmes colonnes que le tableau affiché, comme sur la page des fins de contrat.
        const headers = [
            'Nom', 'Poste', 'Département', 'Date de début', 'Date de fin',
            'Jours expirés', 'Email',
        ];
        // Les valeurs peuvent contenir le séparateur : on les protège.
        const echapper = (valeur: string) =>
            /[;"\n]/.test(valeur) ? `"${valeur.replace(/"/g, '""')}"` : valeur;

        const csvContent = [
            headers.join(';'),
            ...rows.map(contrat => {
                const joursExpires = calculerJoursExpires(contrat.dateFin);
                return [
                    contrat.nom,
                    contrat.poste,
                    contrat.departement || '',
                    formaterDate(contrat.dateDebut),
                    formaterDate(contrat.dateFin),
                    joursExpires.toString(),
                    contrat.email || '',
                ].map(v => echapper(String(v ?? ''))).join(';');
            })
        ].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `contrats_expires_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="p-6 space-y-6 bg-base-100 min-h-screen">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm btn-circle">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <AlertTriangle className="w-8 h-8 text-error" />
                            Contrats Expirés (+30 jours)
                        </h1>
                    </div>
                    <p className="text-base-content/70 mt-1 ml-10">
                        Gestion des contrats expirés depuis plus de 30 jours
                    </p>
                </div>
                <div className="flex gap-2">
                    <button 
                        className="btn btn-outline gap-2"
                        onClick={exporterListeCSV}
                    >
                        <Download className="w-4 h-4" />
                        Exporter CSV
                    </button>
                    <button 
                        className="btn btn-primary gap-2"
                        onClick={ouvrirModalRappel}
                        disabled={contratsSelectionnes.length === 0}
                    >
                        <Bell className="w-4 h-4" />
                        Planifier un rappel ({contratsSelectionnes.length})
                    </button>
                </div>
            </div>

            {/* Statistiques rapides */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="stats shadow">
                    <div className="stat">
                        <div className="stat-figure text-error">
                            <AlertTriangle className="w-8 h-8" />
                        </div>
                        <div className="stat-title">Total Expirés</div>
                        <div className="stat-value text-error">{rows.length}</div>
                        <div className="stat-desc">Contrats terminés depuis +30j</div>
                    </div>
                </div>
                
                <div className="stats shadow">
                    <div className="stat">
                        <div className="stat-figure text-primary">
                            <Bell className="w-8 h-8" />
                        </div>
                        <div className="stat-title">Rappels Actifs</div>
                        <div className="stat-value text-primary">{rappelPlanifies.length}</div>
                        <div className="stat-desc">Notifications programmées</div>
                    </div>
                </div>
            </div>

            {/* Liste des contrats */}
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="loading loading-spinner loading-lg text-primary"></span>
                        </div>
                    ) : error ? (
                        <div className="alert alert-error m-4">
                            <AlertTriangle className="w-6 h-6" />
                            <span>{error}</span>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="text-center p-8 text-base-content/70">
                            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-success" />
                            <p>Aucun contrat expiré depuis plus de 30 jours trouvé.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra w-full">
                                <thead>
                                    <tr>
                                        <th className="w-12">
                                            <label>
                                                <input 
                                                    type="checkbox" 
                                                    className="checkbox"
                                                    checked={contratsSelectionnes.length === rows.length && rows.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setContratsSelectionnes(rows.map(r => r.id));
                                                        } else {
                                                            setContratsSelectionnes([]);
                                                        }
                                                    }}
                                                />
                                            </label>
                                        </th>
                                        <th>Employé</th>
                                        <th>Poste</th>
                                        <th>Département</th>
                                        <th>Date de début</th>
                                        <th>Date de fin</th>
                                        <th>Expiré depuis</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((contrat) => {
                                        const joursExpires = calculerJoursExpires(contrat.dateFin);
                                        
                                        // Logique de couleur demandée :
                                        // 7 jours (récent) -> Vert (Normal)
                                        // 15 jours -> Jaune (Critique)
                                        // 30 jours -> Rouge (Expiration confirmée)
                                        let badgeClass = 'badge-success'; // Par défaut vert (< 15 jours)
                                        if (joursExpires >= 30) {
                                            badgeClass = 'badge-error'; // Rouge
                                        } else if (joursExpires >= 15) {
                                            badgeClass = 'badge-warning'; // Jaune
                                        }

                                        return (
                                            <tr key={contrat.id} className="hover">
                                                <td>
                                                    <label>
                                                        <input 
                                                            type="checkbox" 
                                                            className="checkbox"
                                                            checked={contratsSelectionnes.includes(contrat.id)}
                                                            onChange={() => toggleSelectionContrat(contrat.id)}
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-3">
                                                        <div className="avatar">
                                                            <div className="mask mask-squircle w-10 h-10">
                                                                {contrat.photo ? (
                                                                    <img src={contrat.photo} alt={contrat.nom} />
                                                                ) : (
                                                                    <div className="bg-neutral text-neutral-content w-full h-full flex items-center justify-center">
                                                                        <span className="text-xs">{contrat.nom.substring(0, 2).toUpperCase()}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="font-bold">{contrat.nom}</div>
                                                            <div className="text-sm opacity-50">{contrat.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="badge badge-ghost">{contrat.poste}</div>
                                                </td>
                                                <td>
                                                    <span className="text-sm">{contrat.departement}</span>
                                                </td>
                                                <td>
                                                    <span className="text-sm">
                                                        {contrat.dateDebut ? formaterDate(contrat.dateDebut) : '—'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2 font-medium">
                                                        <Calendar className="w-4 h-4 opacity-70" />
                                                        {formaterDate(contrat.dateFin)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`badge ${badgeClass} gap-2`}>
                                                        <Clock className="w-3 h-3" />
                                                        +{joursExpires} jours
                                                    </div>
                                                </td>
                                                <td>
                                                    <button 
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() => navigate(`/employees/${contrat.employee_pk}`)}
                                                    >
                                                        Voir profil
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de planification de rappel */}
            {modalRappelOpen && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">Planifier un rappel</h3>
                        
                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Employés concernés</span>
                            </label>
                            <div className="p-3 bg-base-200 rounded-lg text-sm max-h-32 overflow-y-auto">
                                {getNomsContratsSelectionnes()}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Date</span>
                                </label>
                                <input 
                                    type="date" 
                                    className="input input-bordered"
                                    value={nouveauRappel.dateRappel}
                                    onChange={(e) => setNouveauRappel({...nouveauRappel, dateRappel: e.target.value})}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Heure</span>
                                </label>
                                <input 
                                    type="time" 
                                    className="input input-bordered"
                                    value={nouveauRappel.heure}
                                    onChange={(e) => setNouveauRappel({...nouveauRappel, heure: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="form-control mb-4">
                            <label className="label">
                                <span className="label-text">Fréquence</span>
                            </label>
                            <select 
                                className="select select-bordered"
                                value={nouveauRappel.frequence}
                                onChange={(e) => setNouveauRappel({...nouveauRappel, frequence: e.target.value as any})}
                            >
                                <option value="ponctuel">Une fois</option>
                                <option value="quotidien">Tous les jours</option>
                                <option value="hebdomadaire">Toutes les semaines</option>
                            </select>
                        </div>

                        <div className="form-control mb-4">
                            <label className="label">
                                <span className="label-text">Méthode de notification</span>
                            </label>
                            <div className="flex gap-4">
                                <label className="label cursor-pointer gap-2">
                                    <input 
                                        type="checkbox" 
                                        className="checkbox"
                                        checked={nouveauRappel.methode.includes('notification')}
                                        onChange={(e) => {
                                            const newMethodes = e.target.checked
                                                ? [...nouveauRappel.methode, 'notification']
                                                : nouveauRappel.methode.filter(m => m !== 'notification');
                                            setNouveauRappel({...nouveauRappel, methode: newMethodes as any});
                                        }}
                                    />
                                    <span className="label-text flex items-center gap-1"><Bell className="w-4 h-4"/> App</span>
                                </label>
                                <label className="label cursor-pointer gap-2">
                                    <input 
                                        type="checkbox" 
                                        className="checkbox"
                                        checked={nouveauRappel.methode.includes('email')}
                                        onChange={(e) => {
                                            const newMethodes = e.target.checked
                                                ? [...nouveauRappel.methode, 'email']
                                                : nouveauRappel.methode.filter(m => m !== 'email');
                                            setNouveauRappel({...nouveauRappel, methode: newMethodes as any});
                                        }}
                                    />
                                    <span className="label-text flex items-center gap-1"><Mail className="w-4 h-4"/> Email</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-control mb-6">
                            <label className="label">
                                <span className="label-text">Message personnalisé</span>
                            </label>
                            <textarea 
                                className="textarea textarea-bordered h-24"
                                value={nouveauRappel.message}
                                onChange={(e) => setNouveauRappel({...nouveauRappel, message: e.target.value})}
                            ></textarea>
                        </div>

                        <div className="modal-action">
                            <button className="btn" onClick={() => setModalRappelOpen(false)}>Annuler</button>
                            <button 
                                className="btn btn-primary" 
                                onClick={planifierRappel}
                                disabled={nouveauRappel.methode.length === 0}
                            >
                                Planifier
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContratsExpires;
